/**
 * Application configuration
 *
 * This module provides access to configuration values that can be set at runtime
 * (via window.APP_CONFIG injected by the Docker container) or at build time
 * (via Vite environment variables for local development).
 *
 * Runtime configuration takes precedence over build-time configuration,
 * allowing the same Docker image to be used across different environments.
 */

import { APP_NAME, APP_FULL_NAME } from './branding';

// Extend Window interface to include our runtime config
declare global {
  interface Window {
    APP_CONFIG?: {
      VITE_APP_TITLE?: string;
      VITE_APP_NAME?: string;
      VITE_APP_FULL_NAME?: string;
      VITE_APP_ENV_LABEL?: string;
      VITE_BACKEND_API?: string;
      VITE_WEBSOCKET_API?: string;
      VITE_GOOGLE_CLIENT_ID?: string;
      VITE_STRIPE_PUBLISHABLE_KEY?: string;
      VITE_ADMIN_URL?: string;
    };
  }
}

/**
 * Get a configuration value, preferring runtime config over build-time config
 */
function getConfig(key: keyof NonNullable<Window['APP_CONFIG']>): string {
  // Check for runtime config first (injected by Docker container)
  const runtimeValue = window.APP_CONFIG?.[key];
  if (runtimeValue) {
    return runtimeValue;
  }

  // Fall back to build-time config (for local development)
  const buildTimeValue = import.meta.env[key];
  if (buildTimeValue) {
    return buildTimeValue;
  }

  // Return empty string if neither is available
  console.warn(`Configuration key "${key}" not found in runtime or build-time config`);
  return '';
}

/**
 * Like {@link getConfig} but falls back to a hard-coded default instead of
 * warning. Used for product branding so the app always has a name even when no
 * env/runtime override is set.
 */
function getConfigWithDefault(key: keyof NonNullable<Window['APP_CONFIG']>, fallback: string): string {
  return window.APP_CONFIG?.[key] || import.meta.env[key] || fallback;
}

// Product branding. The single source is src/branding.ts (used here at runtime
// and by the index.html build hook); an explicit VITE_APP_NAME / VITE_APP_FULL_NAME
// env var still overrides if you need per-deploy branding.
const appName = getConfigWithDefault('VITE_APP_NAME', APP_NAME);
const appFullName = getConfigWithDefault('VITE_APP_FULL_NAME', APP_FULL_NAME);
// Optional per-environment marker appended to the tab title (e.g. "dev",
// "Staging"); empty in production.
const appEnvLabel = getConfigWithDefault('VITE_APP_ENV_LABEL', '');

// Origin of the admin console, for the handoff in `docs/WebAuthn.md` §4.7 — the app has to know
// where to send the minted ticket. Empty where no console is deployed, and the handoff route then
// refuses rather than navigating somewhere invented: a wrong origin here would post a ticket to a
// host we do not control.
const adminUrl = getConfigWithDefault('VITE_ADMIN_URL', '').replace(/\/+$/, '');

export const config = {
  adminUrl,
  appName,
  appFullName,
  appEnvLabel,
  // Browser-tab title — derived from the brand so it flows automatically:
  // "<full name>" or "<full name> (<env label>)". Applied at runtime in App.tsx;
  // index.html shows a build-time first-paint title until the app mounts.
  appTitle: appEnvLabel ? `${appFullName} (${appEnvLabel})` : appFullName,
  backendApi: getConfig('VITE_BACKEND_API'),
  websocketApi: getConfig('VITE_WEBSOCKET_API'),
  googleClientId: getConfig('VITE_GOOGLE_CLIENT_ID'),
  stripePublishableKey: getConfig('VITE_STRIPE_PUBLISHABLE_KEY'),
} as const;
